import React, { useState, useRef, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { 
  Button, 
  Container, 
  ErrorMessage, 
  FormCard, 
  FormContainer, 
  Header, 
  InfoText, 
  InputGroup, 
  ResendLink, 
  SuccessMessage 
} from './styles/stallogin.styles';

const StallLogin = () => {
  const [stallNumber, setStallNumber] = useState(''); // store only digits
  const [telephoneNumber, setTelephoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(0);

  const stallInputRef = useRef<HTMLInputElement>(null);

  // Firebase function URL for Yo Uganda SMS
  const SEND_OTP_FUNCTION_URL = "https://us-central1-grema-62003.cloudfunctions.net/send_otp_via_yo_uganda";

  // keep cursor after prefix
  useEffect(() => {
    if (stallInputRef.current) {
      const prefixLength = 6; // "GREMA-" is 6 chars
      const handleFocus = () => {
        stallInputRef.current!.setSelectionRange(prefixLength, prefixLength);
      };
      stallInputRef.current.addEventListener("focus", handleFocus);
      return () => {
        stallInputRef.current?.removeEventListener("focus", handleFocus);
      };
    }
  }, []);

  const handleStallNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // strip GREMA- and keep only digits
    let numericPart = value.replace(/^GREMA-/, "").replace(/\D/g, "");
    setStallNumber(numericPart);
  };

  const getFullStallNumber = () => `GREMA-${stallNumber}`;

  const normalizePhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If it's a Ugandan number, ensure it's in the +256 format
    if (cleaned.startsWith('0')) {
      cleaned = '256' + cleaned.substring(1);
    }
    
    return cleaned;
  };

  const sendOTPViaYoUganda = async (phone: string, otpCode: string): Promise<boolean> => {
    try {
      const normalizedPhone = normalizePhoneNumber(phone);
      
      console.log('Sending to API - Phone:', normalizedPhone, 'OTP:', otpCode);
      
      const response = await fetch(SEND_OTP_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: normalizedPhone,
          otp: otpCode.toString(),
        }),
      });

      const data = await response.json();
      console.log('SMS API Response:', data);

      if (response.ok && data.status === 'OK') {
        return true;
      } else {
        console.error('Failed to send OTP:', data);
        setError(data.message || 'Failed to send OTP. Please try again.');
        return false;
      }
    } catch (error) {
      console.error('Error calling Firebase function:', error);
      setError('Network error. Please check your connection and try again.');
      return false;
    }
  };

  const handleSubmit = async (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (isOtpSent) {
      await verifyOTP();
    } else {
      await verifyStallAndSendOTP();
    }
  };

  const verifyStallAndSendOTP = async () => {
    try {
      if (!stallNumber || !telephoneNumber) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      // Verify stall exists and phone number matches
      const fullStallNumber = getFullStallNumber();
      const stallsRef = collection(db, 'stalls');
      const q = query(stallsRef, where('stallNumber', '==', fullStallNumber));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Stall number not found. Please check and try again.');
        setLoading(false);
        return;
      }

      const stallData = querySnapshot.docs[0].data();
      const normalizedEntered = normalizePhoneNumber(telephoneNumber);
      
      // Check against business phone and whatsapp phone
      const registeredPhone = stallData.businessPhone || stallData.contact || '';
      const whatsappPhone = stallData.whatsappPhone || '';
      
      const normalizedRegistered = normalizePhoneNumber(registeredPhone);
      const normalizedWhatsapp = normalizePhoneNumber(whatsappPhone);
      
      let isMatch = false;
      if (normalizedRegistered && normalizedEntered === normalizedRegistered) isMatch = true;
      if (!isMatch && normalizedWhatsapp && normalizedEntered === normalizedWhatsapp) isMatch = true;
      
      if (!isMatch) {
        setError('Invalid phone number. Please use the phone number registered with this stall.');
        setLoading(false);
        return;
      }

      // Generate OTP (ensure it's exactly 6 digits)
      const generatedOtp = Math.floor(100000 + Math.random() * 900000).toString();
      
      console.log('Generated OTP:', generatedOtp);
      
      // Send OTP via Firebase function
      const normalizedPhone = normalizePhoneNumber(telephoneNumber);
      const otpSent = await sendOTPViaYoUganda(normalizedPhone, generatedOtp);
      
      if (otpSent) {
        setIsOtpSent(true);
        setSuccess('OTP has been sent to your phone. Please enter it below.');
        startResendCountdown();
        
        // Store the OTP in sessionStorage for verification
        sessionStorage.setItem('stall_otp', generatedOtp);
        sessionStorage.setItem('stall_otp_phone', normalizedPhone);
        sessionStorage.setItem('stall_otp_time', Date.now().toString());
        
        console.log('OTP stored in session:', generatedOtp);
      }
      
    } catch (err: any) {
      console.error("Error during OTP sending process:", err);
      setError('Failed to send OTP. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    try {
      if (!otp || otp.length !== 6) {
        setError('Please enter a valid 6-digit OTP');
        setLoading(false);
        return;
      }

      const storedOtp = sessionStorage.getItem('stall_otp');
      const storedPhone = sessionStorage.getItem('stall_otp_phone');
      const storedTime = sessionStorage.getItem('stall_otp_time');
      const normalizedPhone = normalizePhoneNumber(telephoneNumber);  

      if (!storedOtp || !storedPhone || !storedTime) {
        setError('No OTP session found. Please request a new OTP.');
        setIsOtpSent(false);
        return;
      }

      // Check if OTP expired (10 minutes)
      const storedTimeNum = parseInt(storedTime);
      if (Date.now() - storedTimeNum > 10 * 60 * 1000) {
        setError('OTP has expired. Please request a new one.');
        setIsOtpSent(false);
        return;
      }

      // Phone verification
      if (normalizedPhone !== storedPhone) {
        setError('Phone number does not match. Please try again.');
        return;
      }

      // OTP verification - remove any whitespace and compare
      const cleanEnteredOtp = otp.replace(/\s/g, '');
      const cleanStoredOtp = storedOtp.replace(/\s/g, '');
      
      if (cleanEnteredOtp === cleanStoredOtp) {
        setSuccess('OTP verified successfully! Redirecting...');
        
        // Store authentication in localStorage
        localStorage.setItem('stallAuth', JSON.stringify({
          stallNumber: getFullStallNumber(),
          telephoneNumber: normalizedPhone,
          authenticated: true
        }));
        
        setTimeout(() => {
          // Clear session storage
          sessionStorage.removeItem('stall_otp');
          sessionStorage.removeItem('stall_otp_phone');
          sessionStorage.removeItem('stall_otp_time');
          
          window.location.href = `/user-listings?stallNumber=${getFullStallNumber()}`;
        }, 1500);
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (error: any) {
      console.error("Error during OTP verification:", error);
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    setCanResend(false);

    try {
      // Generate new OTP
      const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
      const normalizedPhone = normalizePhoneNumber(telephoneNumber);
      
      const otpSent = await sendOTPViaYoUganda(normalizedPhone, newOtp);
      
      if (otpSent) {
        setSuccess('New OTP has been sent to your phone.');
        startResendCountdown();
        
        // Update stored OTP
        sessionStorage.setItem('stall_otp', newOtp);
        sessionStorage.setItem('stall_otp_time', Date.now().toString());
      } else {
        setError('Failed to resend OTP. Please try again.');
        setCanResend(true);
      }
    } catch (err) {
      console.error("Error during OTP resend process:", err);
      setError('Failed to resend OTP. Please try again.');
      setCanResend(true);
    } finally {
      setLoading(false);
    }
  };

  const startResendCountdown = () => {
    setCanResend(false);
    let countdown = 30;
    setResendCountdown(countdown);

    const interval = setInterval(() => {
      countdown -= 1;
      setResendCountdown(countdown);
      if (countdown <= 0) {
        clearInterval(interval);
        setCanResend(true);
      }
    }, 1000);
  };

  return (
    <Container>
      <FormCard>
        <Header>
          <h2>Stall Access</h2>
          <p>Please enter your stall details to continue</p>
        </Header>

        <FormContainer>
          <form onSubmit={handleSubmit}>
            {!isOtpSent ? (
              <>
                <InputGroup>
                  <label htmlFor="stallNumber">Stall Number</label>
                  <input
                    ref={stallInputRef}
                    type="text"
                    id="stallNumber"
                    value={getFullStallNumber()}
                    onChange={handleStallNumberChange}
                    required
                    disabled={loading}
                  />
                </InputGroup>

                <InputGroup>
                  <label htmlFor="telephone">Business Telephone Number</label>
                  <input
                    type="tel"
                    id="telephone"
                    placeholder="e.g., 0700562982 or +256700562982"
                    value={telephoneNumber}
                    onChange={(e) => setTelephoneNumber(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <small>Enter your phone number with or without country code</small>
                </InputGroup>
              </>
            ) : (
              <InputGroup>
                <label htmlFor="otp">Enter OTP</label>
                <input
                  type="text"
                  id="otp"
                  placeholder="Enter the 6-digit OTP sent to your phone"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  disabled={loading}
                  maxLength={6}
                />
                <ResendLink>
                  <span>Didn't receive the code? </span>
                  <button 
                    type="button" 
                    onClick={handleResendOTP}
                    disabled={!canResend || loading}
                  >
                    Resend {!canResend && `(${resendCountdown}s)`}
                  </button>
                </ResendLink>
              </InputGroup>
            )}

            <Button type="submit" disabled={loading}>
              {loading ? 'Processing...' : (isOtpSent ? 'Verify OTP' : 'Send OTP')}
            </Button>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}
          </form>

          <InfoText>
            This form verifies your stall details. If your stall is not yet active, 
            you will be redirected to the payment page.
          </InfoText>
        </FormContainer>
      </FormCard>
    </Container>
  );
};

export default StallLogin;
